import { ApplicationRef, Injectable, OnDestroy } from '@angular/core';
import { Socket, SocketIoConfig } from 'ngx-socket-io';
import { BehaviorSubject, EMPTY, filter, map, Observable, Subject, Subscription } from 'rxjs';
import { AstimaxSocketEvent } from '../../interfaces/socket-event.interface';
import { AuthService } from 'core-client/api';
import { OAuthService } from 'angular-oauth2-oidc';

type SocketIoConnectionState = 'disconnected' | 'connecting' | 'connected';

interface WidgetReference {
	widgetId: string;
	dashboardId: string;
	subscriptions: Set<string>;
}

/**
 * Manages a single socket.on handler per event name, dispatching via Subject.
 * Bypasses ngx-socket-io's fromEvent() to avoid its subscribersCounter leak
 * that causes duplicate handlers on reconnection.
 */
interface ManagedEventSource {
	subject: Subject<unknown>;
	handler: (data: unknown) => void;
	isAttached: boolean;
}

interface ConnectionError {
	timestamp: number;
	message: string;
	count: number;
	lastLoggedAt: number;
}

/**
 * Connection health states - matches auth service naming for consistency
 */
enum ConnectionHealthState {
	HEALTHY = 'HEALTHY',           // Normal operation - allow connections
	DEGRADED = 'DEGRADED',         // Too many failures - block connections
	RECOVERING = 'RECOVERING'      // Testing recovery - allow one attempt
}

interface ConnectionHealthTracker {
	state: ConnectionHealthState;
	failureCount: number;
	lastFailureTime: number;
	nextAttemptTime: number;
}

@Injectable({
	providedIn: 'root'
})
export class SocketIoService extends Socket implements OnDestroy {
	// Error tracking configuration
	private readonly ERROR_LOG_THROTTLE_MS: number = 30000;
	private readonly MAX_CONSECUTIVE_ERRORS: number = 3;

	// Connection health configuration
	private readonly HEALTH_FAILURE_THRESHOLD = 5;
	private readonly HEALTH_RECOVERY_TIMEOUT = 120000;  // 2 minutes

	// Error tracking
	private lastError?: ConnectionError;
	private errorCount: number = 0;
	private consecutiveErrorCount: number = 0;
	private errorLoggingSuppressed: boolean = false;

	// Widget management
	private readonly registeredWidgets: Map<string, WidgetReference> = new Map();
	private readonly unregisterCallbacks: Map<string, () => void> = new Map();
	private readonly connectionState$: BehaviorSubject<SocketIoConnectionState> =
		new BehaviorSubject<SocketIoConnectionState>('disconnected');

	// Event sources — one socket.on handler per event name, dispatched via Subject
	private readonly managedEventSources: Map<string, ManagedEventSource> = new Map();
	private readonly recentEventIds: Set<string> = new Set();

	// Auth subscriptions
	private oauthEventsSubscription?: Subscription;
	private networkSubscription?: Subscription;

	// Token tracking
	private currentToken: string | null = null;

	// Connection health tracker
	private connectionHealthTracker: ConnectionHealthTracker = {
		state: ConnectionHealthState.HEALTHY,
		failureCount: 0,
		lastFailureTime: 0,
		nextAttemptTime: 0
	};

	constructor(
		private readonly authService: AuthService,
		private readonly oauthService: OAuthService,
		appRef: ApplicationRef
	) {
		// Initialize with current token
		const token = authService.getAccessToken();
		super(SocketIoService.createSocketConfig(token), appRef);
		this.currentToken = token;

		this.setupSocketEvents();
		this.setupAuthenticationHandling();
		this.setupNetworkHandling();
	}

	/**
	 * Create socket configuration with authentication
	 */
	private static createSocketConfig(token?: string | null): SocketIoConfig {
		return {
			url: `${document.location.protocol}//${document.location.hostname}:${document.location.port}`,
			options: {
				path: '/addboard-gateway/ws',
				autoConnect: false,
				reconnection: true,
				randomizationFactor: 0.5,  // Add ±50% jitter to prevent thundering herd
				extraHeaders: token ? { 'Authorization': `Bearer ${token}` } : {},
				// Use query for WebSocket upgrade (persists across upgrade)
				query: token ? { token } : {}
			}
		};
	}

	/**
	 * Setup authentication handling with token lifecycle coordination
	 */
	private setupAuthenticationHandling(): void {
		this.oauthEventsSubscription = this.oauthService.events.subscribe(event => {
			if (event.type === 'token_refreshed') {
				const newToken = this.authService.getAccessToken();

				console.log('[Astimax Addboard Gateway] Socket - Token refreshed, updating configuration');

				// Update token for next connection
				// Socket.IO connection persists until disconnect, token is only used during handshake
				this.currentToken = newToken;
				this.updateSocketConfig(newToken);

				// If we were stuck due to token issues and have widgets, try reconnecting
				if (!this.connected && this.registeredWidgets.size > 0) {
					console.log('[Astimax Addboard Gateway] Socket - Attempting reconnection with refreshed token');
					setTimeout(() => {
						this.ensureConnection();
					}, 1000);
				}
			}
			else if (event.type === 'token_received') {
				const newToken = this.authService.getAccessToken();
				console.log('[Astimax Addboard Gateway] Socket - Token received, ready for connection');

				this.currentToken = newToken;
				this.updateSocketConfig(newToken);

				// If we have widgets waiting, connect now
				if (!this.connected && this.registeredWidgets.size > 0) {
					console.log('[Astimax Addboard Gateway] Socket - Connecting with new token');
					setTimeout(() => {
						this.ensureConnection();
					}, 500);
				}
			}
			else if (event.type === 'session_terminated' || event.type === 'session_error') {
				console.log('[Astimax Addboard Gateway] Socket - Session ended, clearing auth');
				this.handleSessionTerminated();
			}
		});

		// Handle auth errors from backend
		this.on('connect_error', (error: { message: string }) => {
			if (error.message === 'Authentication error' ||
				error.message === 'Unauthorized' ||
				error.message.includes('401') ||
				error.message.includes('403')) {
				console.error('[Astimax Addboard Gateway] Socket - Backend rejected authentication');
				this.handleBackendAuthRejection();
			}
		});

		this.on('auth_required', () => {
			console.warn('[Astimax Addboard Gateway] Socket - Backend requires authentication');
		});

		this.on('auth_invalid', () => {
			console.error('[Astimax Addboard Gateway] Socket - Backend rejected token');
			this.handleBackendAuthRejection();
		});
	}

	/**
	 * Handle backend authentication rejection
	 * Don't disconnect unnecessarily - Keycloak restart ≠ invalid token
	 */
	private handleBackendAuthRejection(): void {
		const freshToken = this.authService.getAccessToken();
		const tokenChanged = freshToken !== this.currentToken;
		const providerAvailable = this.authService.isProviderAvailable();

		console.warn('[Astimax Addboard Gateway] Socket - Backend rejected authentication', {
			hasToken: !!freshToken,
			tokenChanged: tokenChanged,
			providerAvailable: providerAvailable
		});

		// No token at all - wait for token_received event
		if (!freshToken) {
			console.log('[Astimax Addboard Gateway] Socket - No token available, will reconnect when token received');

			if (this.connected) {
				this.disconnect();
			}

			this.connectionState$.next('disconnected');
			return;
		}

		// Token changed - reconnect with new token
		if (tokenChanged) {
			console.log('[Astimax Addboard Gateway] Socket - Token changed, reconnecting with fresh token');
			this.currentToken = freshToken;
			this.updateSocketConfig(freshToken);

			if (this.connected) {
				this.disconnect();
			}

			setTimeout(() => {
				this.ensureConnection();
			}, 1000);
			return;
		}

		// Same token, provider unavailable
		// Keep connection alive - backend will accept token when Keycloak returns
		if (!providerAvailable) {
			console.warn(
				'[Astimax Addboard Gateway] Socket - Auth provider unavailable ' +
				'(Keycloak restarting?) - keeping connection, backend will recover'
			);
			// No disconnect - Socket.IO will automatically retry
			// Backend will accept the token again when Keycloak comes back online
			return;
		}

		// Same token, provider available, backend still rejecting
		// Might be token revoked or temporary backend issue
		// Let Socket.IO handle reconnection, wait for token_refreshed event
		console.warn(
			'[Astimax Addboard Gateway] Socket - Backend rejecting token (might be revoked) - ' +
			'waiting for token refresh'
		);
		// No disconnect - wait for AuthService to refresh token
		// If token is revoked, token_refresh will fail and session will terminate
	}

	/**
	 * Handle session termination (logout)
	 */
	private handleSessionTerminated(): void {
		console.log('[Astimax Addboard Gateway] Socket - Session terminated, disconnecting');

		if (this.connected) {
			this.disconnect();
		}

		this.connectionState$.next('disconnected');
		this.currentToken = null;
		this.updateSocketConfig(null);
	}

	/**
	 * Setup network status monitoring
	 */
	private setupNetworkHandling(): void {
		this.networkSubscription = this.authService.isOnline$().subscribe(isOnline => {
			if (isOnline && this.registeredWidgets.size > 0) {
				console.log('[Astimax Addboard Gateway] Socket - Network reconnected, checking connection');

				const freshToken = this.authService.getAccessToken();

				if (freshToken !== this.currentToken) {
					console.log('[Astimax Addboard Gateway] Socket - Token changed during outage, updating');
					this.currentToken = freshToken;
					this.updateSocketConfig(freshToken);
				}

				// Reset connection health on network restore
				if (this.isConnectionDegraded()) {
					console.log('[Astimax Addboard Gateway] Socket - Network restored, resetting connection health');
					this.resetConnectionHealth();
				}

				if (!this.connected && this.connectionState$.value === 'disconnected') {
					console.log('[Astimax Addboard Gateway] Socket - Reconnecting after network restore');
					this.ensureConnection();
				}
			}
		});
	}

	/**
	 * Update socket config with new token (for next connection)
	 */
	private updateSocketConfig(token: string | null): void {
		if (token) {
			this.ioSocket.io.opts.extraHeaders = { 'Authorization': `Bearer ${token}` };
			this.ioSocket.io.opts.query = { token };
		} else {
			this.ioSocket.io.opts.extraHeaders = {};
			this.ioSocket.io.opts.query = {};
		}
	}

	/**
	 * Check if connection is degraded
	 */
	private isConnectionDegraded(): boolean {
		return this.connectionHealthTracker.state === ConnectionHealthState.DEGRADED;
	}

	/**
	 * Check if connection attempt is allowed by health tracker
	 */
	private canAttemptConnection(): boolean {
		const now = Date.now();

		switch (this.connectionHealthTracker.state) {
			case ConnectionHealthState.HEALTHY:
				// Normal operation - allow connection
				return true;

			case ConnectionHealthState.DEGRADED:
				// Too many failures - check if timeout has passed
				if (now >= this.connectionHealthTracker.nextAttemptTime) {
					console.log('[Astimax Addboard Gateway] Socket - Connection health check timeout elapsed, entering RECOVERING state');
					this.connectionHealthTracker.state = ConnectionHealthState.RECOVERING;
					return true;
				}
				return false;

			case ConnectionHealthState.RECOVERING:
				// Testing recovery - allow one attempt
				return true;

			default:
				return false;
		}
	}

	/**
	 * Update connection health on connection failure
	 */
	private markConnectionDegraded(): void {
		const now = Date.now();

		this.connectionHealthTracker.failureCount++;
		this.connectionHealthTracker.lastFailureTime = now;

		if (this.connectionHealthTracker.state === ConnectionHealthState.RECOVERING) {
			// Failed during testing - go back to DEGRADED
			console.warn('[Astimax Addboard Gateway] Socket - Connection health re-degraded after failed recovery test');
			this.connectionHealthTracker.state = ConnectionHealthState.DEGRADED;
			this.connectionHealthTracker.nextAttemptTime = now + this.HEALTH_RECOVERY_TIMEOUT;
			return;
		}

		// Check if we should mark connection as degraded
		if (this.connectionHealthTracker.failureCount >= this.HEALTH_FAILURE_THRESHOLD) {
			console.warn(
				`%c[Astimax Addboard Gateway] Socket - Connection marked as DEGRADED after ${this.connectionHealthTracker.failureCount} failures ` +
				`(will test connection in ${this.HEALTH_RECOVERY_TIMEOUT / 1000}s)`,
				'color: #FF5722; font-weight: bold'
			);

			this.connectionHealthTracker.state = ConnectionHealthState.DEGRADED;
			this.connectionHealthTracker.nextAttemptTime = now + this.HEALTH_RECOVERY_TIMEOUT;
		}
	}

	/**
	 * Reset connection health on successful connection
	 */
	private resetConnectionHealth(): void {
		if (this.connectionHealthTracker.failureCount > 0 ||
			this.connectionHealthTracker.state !== ConnectionHealthState.HEALTHY) {
			console.log('[Astimax Addboard Gateway] Socket - Connection health reset to HEALTHY');
		}

		this.connectionHealthTracker = {
			state: ConnectionHealthState.HEALTHY,
			failureCount: 0,
			lastFailureTime: 0,
			nextAttemptTime: 0
		};
	}

	/**
	 * Setup socket events with connection health integration
	 */
	private setupSocketEvents(): void {
		this.on('connect', (): void => {
			if (this.errorLoggingSuppressed || this.consecutiveErrorCount > 0) {
				console.log('%c[Astimax Addboard Gateway] Socket - Connected (recovered)',
					'color: #4CAF50; font-weight: bold');
			} else {
				console.log('[Astimax Addboard Gateway] Socket - Connected');
			}

			this.connectionState$.next('connected');
			this.resetErrorTracking();
			this.resetConnectionHealth();

			// Attach all managed event handlers on (re)connect
			this.managedEventSources.forEach((source: ManagedEventSource, eventName: string) => {
				this.attachEventHandler(eventName, source);
			});
		});

		this.on('disconnect', (reason: string): void => {
			console.log(`[Astimax Addboard Gateway] Socket - Disconnected: ${reason}`);
			this.connectionState$.next('disconnected');

			// Socket.IO removes listeners on disconnect — mark all as detached
			this.managedEventSources.forEach((source: ManagedEventSource) => {
				source.isAttached = false;
			});
		});

		this.on('connect_error', (err: { message: string }): void => {
			// Update connection health on failure
			this.markConnectionDegraded();

			// Auth errors handled in setupAuthenticationHandling()
			if (err.message === 'Authentication error' || err.message === 'Unauthorized') {
				return;
			}

			this.logConnectionError(err);
		});

		this.ioSocket.io.on('reconnect', (attemptNumber: number): void => {
			if (this.errorLoggingSuppressed) {
				console.log(`%c[Astimax Addboard Gateway] Socket - Reconnected after ${attemptNumber} attempt(s)`,
					'color: #4CAF50; font-weight: bold');
			} else {
				console.log(`[Astimax Addboard Gateway] Socket - Reconnected after ${attemptNumber} attempt(s)`);
			}

			this.resetErrorTracking();
			this.resetConnectionHealth();
		});

		this.ioSocket.io.on('reconnect_attempt', (attemptNumber: number): void => {
			if (attemptNumber === 1) {
				console.log(`[Astimax Addboard Gateway] Socket - Attempting to reconnect...`);
			} else if (attemptNumber % 10 === 0 && !this.errorLoggingSuppressed) {
				console.log(`[Astimax Addboard Gateway] Socket - Reconnection attempt #${attemptNumber}`);
			}
		});

		this.ioSocket.io.on('reconnect_failed', (): void => {
			console.error('[Astimax Addboard Gateway] Socket - All reconnection attempts exhausted');

			// Update connection health
			this.markConnectionDegraded();

			if (!this.errorLoggingSuppressed) {
				console.warn(
					'[Astimax Addboard Gateway] Socket - Cannot establish connection. ' +
					'Will retry when network/provider becomes available.'
				);
			}
		});

		this.ioSocket.io.on('error', (err: any): void => {
			const sanitizedMessage = this.sanitizeErrorMessage(err.message || err.toString());
			const errorType = this.getErrorType(sanitizedMessage);

			const now = Date.now();
			this.consecutiveErrorCount++;

			if (this.consecutiveErrorCount > this.MAX_CONSECUTIVE_ERRORS) {
				if (!this.errorLoggingSuppressed) {
					console.warn(
						`[Astimax Addboard Gateway] Socket - Suppressing errors (${errorType}). ` +
						`Will notify when restored.`
					);
					this.errorLoggingSuppressed = true;
				}
				return;
			}

			if (this.lastError?.message === sanitizedMessage) {
				this.lastError.count++;

				if (now - this.lastError.lastLoggedAt > this.ERROR_LOG_THROTTLE_MS) {
					console.error(
						`[Astimax Addboard Gateway] Socket - Error (${errorType}, ` +
						`${this.lastError.count}x): ${sanitizedMessage}`
					);
					this.lastError.lastLoggedAt = now;
				}
			} else {
				console.error(
					`[Astimax Addboard Gateway] Socket - Error (${errorType}): ${sanitizedMessage}`
				);

				this.lastError = {
					timestamp: now,
					message: sanitizedMessage,
					count: 1,
					lastLoggedAt: now
				};
			}
		});
	}

	registerWidget(widgetId: string, dashboardId: string): void {
		console.log(`[Astimax Addboard Gateway] Socket - Registering widget: ${widgetId}`);

		this.registeredWidgets.set(widgetId, {
			widgetId,
			dashboardId,
			subscriptions: new Set(),
		});

		this.ensureConnection();
	}

	registerUnregisterCallback(widgetId: string, callback: () => void): void {
		this.unregisterCallbacks.set(widgetId, callback);
	}

	unregisterWidget(widgetId: string): void {
		console.log(`[Astimax Addboard Gateway] Socket - Unregistering widget: ${widgetId}`);

		// Fire callback BEFORE removing from registeredWidgets so the widget service
		// can still emit (e.g. server unsubscribe) via this widget
		const callback = this.unregisterCallbacks.get(widgetId);
		if (callback) {
			callback();
			this.unregisterCallbacks.delete(widgetId);
		}

		const widget = this.registeredWidgets.get(widgetId);
		if (widget) {
			this.registeredWidgets.delete(widgetId);

			widget.subscriptions.forEach((eventName: string) => {
				this.cleanupManagedEvent(eventName);
			});
		}

		this.checkConnectionNeed();
	}

	cleanupInactiveWidgets(activeDashboardId: string): void {
		console.log(`[Astimax Addboard Gateway] Socket - Cleaning inactive widgets for dashboard: ${activeDashboardId}`);

		const widgetsToRemove: string[] = [];

		this.registeredWidgets.forEach((widget: WidgetReference, widgetId: string) => {
			if (widget.dashboardId !== activeDashboardId) {
				widgetsToRemove.push(widgetId);
			}
		});

		widgetsToRemove.forEach((widgetId: string) => {
			this.unregisterWidget(widgetId);
		});

		this.cleanupOrphanedManagedEvents();
	}

	/**
	 * Clean up managed event sources not used by any widget
	 */
	private cleanupOrphanedManagedEvents(): void {
		const eventsInUse = new Set<string>();

		this.registeredWidgets.forEach((widget: WidgetReference) => {
			widget.subscriptions.forEach((eventName: string) => {
				eventsInUse.add(eventName);
			});
		});

		let removedCount = 0;
		this.managedEventSources.forEach((source: ManagedEventSource, eventName: string) => {
			if (!eventsInUse.has(eventName)) {
				this.detachEventHandler(eventName, source);
				source.subject.complete();
				this.managedEventSources.delete(eventName);
				removedCount++;
			}
		});

		if (removedCount > 0) {
			console.log(
				`[Astimax Addboard Gateway] Socket - Cleaned up ${removedCount} orphaned event source(s)`
			);
		}
	}

	/**
	 * Listen to server events.
	 * Uses a single socket.on handler per event name with Subject-based multicast.
	 * Bypasses ngx-socket-io's fromEvent() to avoid its subscribersCounter leak.
	 */
	onEvent(eventName: string, widgetId: string): Observable<unknown> {
		if (!this.registeredWidgets.has(widgetId)) {
			console.warn(`[Astimax Addboard Gateway] Socket - Widget ${widgetId} not registered`);
			return EMPTY;
		}

		const widget = this.registeredWidgets.get(widgetId)!;
		widget.subscriptions.add(eventName);

		if (!this.managedEventSources.has(eventName)) {
			const subject = new Subject<unknown>();
			const handler = (data: unknown) => {
				const eventId = (data as { id?: string })?.id;
				if (eventId) {
					if (this.recentEventIds.has(eventId)) return;
					this.recentEventIds.add(eventId);
					setTimeout(() => this.recentEventIds.delete(eventId), 5000);
				}
				subject.next(data);
			};

			const source: ManagedEventSource = { subject, handler, isAttached: false };
			this.managedEventSources.set(eventName, source);

			// Attach immediately if already connected
			if (this.ioSocket.connected) {
				this.attachEventHandler(eventName, source);
			}
		}

		return this.managedEventSources.get(eventName)!.subject.asObservable();
	}

	onServiceEvent<T extends AstimaxSocketEvent>(
		channelName: string,
		widgetId: string,
		service: string,
		methods: string | string[]
	): Observable<T> {
		const methodArray = Array.isArray(methods) ? methods : [methods];
		return this.onEvent(channelName, widgetId).pipe(
			filter((event: unknown) => {
				const e = event as AstimaxSocketEvent;
				return (
					e?.service === service &&
					e?.type === channelName && // API design: envelope type always matches channel name
					methodArray.includes(e?.method ?? '')
				);
			}),
			map((event: unknown) => event as T)
		);
	}

	/**
	 * Emit event to server
	 */
	emitEvent(eventName: string, data: any, widgetId: string): void {
		if (!this.registeredWidgets.has(widgetId)) {
			console.warn(`[Astimax Addboard Gateway] Socket - Widget ${widgetId} not registered`);
			return;
		}

		if (this.connected) {
			const token = this.authService.getAccessToken();
			const eventData = token ? { ...data, _auth_token: token } : data;
			this.emit(eventName, eventData);
		} else {
			console.warn(`[Astimax Addboard Gateway] Socket - Cannot emit ${eventName}, not connected`);
		}
	}

	/**
	 * Get connection state observable
	 */
	getConnectionState(): Observable<'disconnected' | 'connecting' | 'connected'> {
		return this.connectionState$.asObservable();
	}

	/**
	 * Get registered widgets
	 */
	getRegisteredWidgets(): string[] {
		return Array.from(this.registeredWidgets.keys());
	}

	/**
	 * Get connection health state for debugging
	 */
	getConnectionHealthState(): ConnectionHealthTracker {
		return { ...this.connectionHealthTracker };
	}

	private checkConnectionNeed(): void {
		const hasRegisteredWidgets = this.registeredWidgets.size > 0;

		if (!hasRegisteredWidgets && this.connected) {
			console.log('[Astimax Addboard Gateway] Socket - No widgets, disconnecting');
			this.disconnect();
			this.connectionState$.next('disconnected');
			this.cleanupAllManagedEvents();
		}
	}

	/**
	 * Enhanced connection logic with health tracker and provider availability check
	 */
	private ensureConnection(): void {
		const shouldConnect = this.registeredWidgets.size > 0 &&
			!this.connected &&
			this.connectionState$.value !== 'connecting';

		if (!shouldConnect) {
			return;
		}

		// Check connection health - don't attempt if degraded or waiting for recovery
		if (!this.canAttemptConnection()) {
			const waitTime = Math.round((this.connectionHealthTracker.nextAttemptTime - Date.now()) / 1000);
			console.warn(
				`[Astimax Addboard Gateway] Socket - Connection health ${this.connectionHealthTracker.state}, ` +
				`deferring connection attempt (retry in ${waitTime}s)`
			);
			return;
		}

		// Check OAuth provider availability - don't attempt if Keycloak is down
		// This prevents connection attempts when we can't get/refresh tokens
		if (!this.authService.isProviderAvailable()) {
			console.warn('[Astimax Addboard Gateway] Socket - OAuth provider unavailable, deferring connection attempt');
			return;
		}

		// Validate token before attempting connection
		const token = this.authService.getAccessToken();

		if (!token) {
			console.warn('[Astimax Addboard Gateway] Socket - No valid token available yet, deferring connection');
			return;
		}

		// Update token if it changed
		if (token !== this.currentToken) {
			console.log('[Astimax Addboard Gateway] Socket - Updating token before connection');
			this.currentToken = token;
			this.updateSocketConfig(token);
		}

		console.log('[Astimax Addboard Gateway] Socket - Connecting...');
		this.connectionState$.next('connecting');
		this.connect();
	}

	/**
	 * Attach a single socket.on handler for an event name
	 */
	private attachEventHandler(eventName: string, source: ManagedEventSource): void {
		if (source.isAttached) return;
		this.ioSocket.on(eventName, source.handler);
		source.isAttached = true;
	}

	/**
	 * Detach the socket.on handler for an event name
	 */
	private detachEventHandler(eventName: string, source: ManagedEventSource): void {
		if (!source.isAttached) return;
		this.ioSocket.removeListener(eventName, source.handler);
		source.isAttached = false;
	}

	/**
	 * Clean up a managed event source if no widget still uses it
	 */
	private cleanupManagedEvent(eventName: string): void {
		for (const widget of this.registeredWidgets.values()) {
			if (widget.subscriptions.has(eventName)) {
				return;
			}
		}

		const source = this.managedEventSources.get(eventName);
		if (source) {
			this.detachEventHandler(eventName, source);
			source.subject.complete();
			this.managedEventSources.delete(eventName);
		}
	}

	private cleanupAllManagedEvents(): void {
		this.managedEventSources.forEach((source: ManagedEventSource, eventName: string) => {
			this.detachEventHandler(eventName, source);
			source.subject.complete();
		});
		this.managedEventSources.clear();
		this.recentEventIds.clear();
	}

	forceCleanup(): void {
		console.log('[Astimax Addboard Gateway] Socket - Force cleanup');
		this.registeredWidgets.clear();
		this.unregisterCallbacks.clear();
		this.cleanupAllManagedEvents();

		if (this.connected) {
			this.disconnect();
		}

		this.connectionState$.next('disconnected');
	}

	ngOnDestroy(): void {
		if (this.oauthEventsSubscription) {
			this.oauthEventsSubscription.unsubscribe();
		}

		if (this.networkSubscription) {
			this.networkSubscription.unsubscribe();
		}

		this.forceCleanup();
		this.connectionState$.complete();
	}

	private sanitizeErrorMessage(message: string): string {
		return message
			.replace(/token=[^&\s]+/g, 'token=<redacted>')
			.replace(/Bearer\s+[^\s]+/g, 'Bearer <redacted>');
	}

	private resetErrorTracking(): void {
		this.lastError = undefined;
		this.errorCount = 0;
		this.consecutiveErrorCount = 0;
		this.errorLoggingSuppressed = false;
	}

	private logConnectionError(error: any): void {
		const now = Date.now();
		const sanitizedMessage = this.sanitizeErrorMessage(error.message || error.toString());
		const errorType = this.getErrorType(sanitizedMessage);

		this.errorCount++;
		this.consecutiveErrorCount++;

		if (this.consecutiveErrorCount > this.MAX_CONSECUTIVE_ERRORS) {
			if (!this.errorLoggingSuppressed) {
				console.warn(
					`[Astimax Addboard Gateway] Socket - Suppressing errors (${errorType}). ` +
					`Will notify when restored.`
				);
				this.errorLoggingSuppressed = true;
			}
			return;
		}

		if (this.lastError?.message === sanitizedMessage) {
			this.lastError.count++;

			if (now - this.lastError.lastLoggedAt > this.ERROR_LOG_THROTTLE_MS) {
				console.warn(
					`[Astimax Addboard Gateway] Socket - Connection error (${errorType}, ` +
					`${this.lastError.count}x): ${sanitizedMessage}`
				);
				this.lastError.lastLoggedAt = now;
			}
		} else {
			console.error(
				`[Astimax Addboard Gateway] Socket - Connection error (${errorType}): ${sanitizedMessage}`
			);

			this.lastError = {
				timestamp: now,
				message: sanitizedMessage,
				count: 1,
				lastLoggedAt: now
			};
		}

		if (this.consecutiveErrorCount === this.MAX_CONSECUTIVE_ERRORS) {
			console.info(
				`[Astimax Addboard Gateway] Socket - Will suppress further errors. ` +
				`Automatic reconnection active.`
			);
		}
	}

	/**
	 * Extract error type from error message for better categorization
	 */
	private getErrorType(message: string): string {
		if (message.includes('ECONNREFUSED')) return 'Connection Refused';
		if (message.includes('ETIMEDOUT')) return 'Timeout';
		if (message.includes('ENETUNREACH')) return 'Network Unreachable';
		if (message.includes('ENOTFOUND')) return 'Host Not Found';
		if (message.includes('timeout')) return 'Timeout';
		if (message.includes('403')) return 'Forbidden';
		if (message.includes('401')) return 'Unauthorized';
		return 'Connection Error';
	}
}
